import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMemberApp } from '../AppProvider';
import { IconCheck, IconDoc, IconMic, IconSend, IconWrench } from './Icons';
import ToolsMenu from './ToolsMenu';
import ProBadge from '@/components/ProBadge';
import { apiUrl } from '@/config/api';
import { getAuthToken } from '@/utils/auth';
import { transcribeVoiceNote } from '@/voice/byoncoVoice';
import { listPendingDocuments, rememberPendingDocuments } from '../localStore';
import { memberPath, wwwUrl, goProduct } from '@/config/hosts';

const UPLOAD_COPY = {
  uploading: {
    title: 'Uploading your report…',
    detail: 'Sending it securely.',
  },
  scanning: {
    title: 'Scanning your document…',
    detail: 'Give us a minute. We are making sure this file is safe to use.',
  },
  reading: {
    title: 'Reading your report…',
    detail: 'This can take a minute. Vani is pulling out the text so she can use it.',
  },
  ready: {
    title: 'Your report is attached.',
    detail: 'Ask about the numbers, medicines, or what to take to clinic. Vani will read this file.',
  },
  error: {
    title: 'We could not attach this file.',
    detail: 'Try again with a PDF or a clearer photo.',
  },
};

const BUSY_PHASES = new Set(['uploading', 'scanning', 'reading']);
const POLL_MS = 1200;
const POLL_TIMEOUT_MS = 90000;
const VOICE_MAX_SEC = 30;

function authHeaders() {
  const headers = {};
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (!signal) return;
    if (signal.aborted) {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

function uploadErrorMessage(status, data) {
  const detail = data?.detail;
  const code = typeof detail === 'object' ? detail?.error : '';
  const text = typeof detail === 'string' ? detail : detail?.error || data?.error;
  if (status === 429 || /too many/i.test(String(text || ''))) {
    return 'Too many uploads just now. Wait a minute and try again.';
  }
  if (code === 'file_quarantined' || status === 422) {
    return 'This file could not be used. Try a different PDF or a clearer photo.';
  }
  if (typeof text === 'string' && text.trim() && text.length < 180 && !/^Request failed/i.test(text)) {
    return text;
  }
  return 'Upload failed. The file was not sent to Vani.';
}

function voiceErrorMessage(error) {
  const status = error?.status;
  const detail = error?.payload?.detail;
  const code = typeof detail === 'object' ? detail?.error : '';
  const text = typeof detail === 'object' ? detail?.message : detail;
  if (status === 402 || code === 'upgrade_required') return '';
  if (status === 429) return 'Too many voice notes just now. Wait a minute and try again.';
  if (status === 503 || code === 'stt_unavailable') {
    return text || 'Voice transcription is not available right now. Try typing.';
  }
  if (code === 'stt_failed' || status === 422) {
    return text || 'We could not transcribe that. Tap Voice, speak for a couple of seconds, then tap Voice again.';
  }
  if (typeof text === 'string' && text.trim() && text.length < 180) return text;
  return 'Voice did not go through. Check the microphone and try again.';
}

function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function cleanMime(type) {
  return String(type || 'audio/webm').split(';')[0].trim() || 'audio/webm';
}

function stopTracks(stream) {
  (stream?.getTracks() || []).forEach((track) => track.stop());
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function listenInBrowser() {
  return new Promise((resolve, reject) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      const error = new Error('no_browser_stt');
      error.code = 'no_browser_stt';
      reject(error);
      return;
    }
    const rec = new SR();
    rec.interimResults = false;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.lang = navigator.language || 'en-IN';
    const timer = setTimeout(() => {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }, 12000);
    rec.onresult = (event) => {
      clearTimeout(timer);
      const said = event.results?.[0]?.[0]?.transcript || '';
      if (said.trim()) resolve(said.trim());
      else {
        const error = new Error('empty');
        error.code = 'empty';
        reject(error);
      }
    };
    rec.onerror = (event) => {
      clearTimeout(timer);
      reject(event);
    };
    rec.onend = () => clearTimeout(timer);
    try {
      rec.start();
    } catch (error) {
      clearTimeout(timer);
      reject(error);
    }
  });
}

function formatElapsed(seconds) {
  const total = Math.max(0, seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

async function waitUntilReadable(documentId, headers, signal) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const response = await fetch(apiUrl(`/api/documents/${documentId}`), {
      credentials: 'include',
      headers,
      signal,
    });
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      const status = data.extraction_status;
      if (status === 'complete' || status === 'failed' || status === 'blocked') return data;
    }
    await sleep(POLL_MS, signal);
  }
  const error = new Error('timeout');
  error.code = 'timeout';
  throw error;
}

function UploadStatus({ phase, filename, errorDetail }) {
  if (!phase) return null;
  const copy = UPLOAD_COPY[phase] || UPLOAD_COPY.uploading;
  const busy = BUSY_PHASES.has(phase);
  const detail = phase === 'error' ? errorDetail || copy.detail : copy.detail;
  return (
    <div
      className={`by-upload-status is-${phase}`}
      role="status"
      aria-live="polite"
      aria-busy={busy}
    >
      <span className="by-upload-status-mark" aria-hidden="true">
        {busy ? <span className="by-upload-spinner" /> : phase === 'ready' ? <IconCheck /> : <IconDoc />}
      </span>
      <div className="by-upload-status-copy">
        <p className="by-upload-status-title">{copy.title}</p>
        <p className="by-upload-status-detail">{detail}</p>
        {filename ? <p className="by-upload-status-file">{filename}</p> : null}
      </div>
    </div>
  );
}

export default function Composer({
  onSend,
  disabled,
  placeholder = 'Ask about treatment, food, medicines, or clinic',
  conversationId,
  autoFocus = true,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, setUpgradeOpen } = useMemberApp();
  const [value, setValue] = useState('');
  const [toolId, setToolId] = useState('');
  const [attachedDocs, setAttachedDocs] = useState([]);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [voicePhase, setVoicePhase] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [voiceError, setVoiceError] = useState('');
  const [uploadPhase, setUploadPhase] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const ref = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);
  const stoppingRef = useRef(false);
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const toolsRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  useEffect(() => {
    if (!disabled && autoFocus) ref.current?.focus();
  }, [disabled, autoFocus]);

  useEffect(() => () => {
    abortRef.current?.abort();
    clearInterval(tickRef.current);
    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }
    stopTracks(streamRef.current);
  }, []);

  useEffect(() => {
    if (!toolsOpen) return undefined;
    const onPointer = (event) => {
      if (toolsRef.current?.contains(event.target)) return;
      if (event.target.closest?.('.by-chip-tools')) return;
      setToolsOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setToolsOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  useEffect(() => {
    const pending = listPendingDocuments(user);
    if (pending.length) setAttachedDocs(pending);
  }, [user]);

  const sendMeta = (channel) => ({
    documentIds: attachedDocs.map((row) => row.id).filter(Boolean),
    tool: toolId || undefined,
    channel: channel || 'text',
  });

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text, sendMeta('text'));
    setValue('');
    setToolId('');
  };

  const uploading = BUSY_PHASES.has(uploadPhase);
  const recording = voicePhase === 'recording';
  const transcribing = voicePhase === 'transcribing';

  const uploadReport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setUploadName(file.name);
    setUploadError('');
    setUploadPhase('uploading');
    const scanTimer = setTimeout(() => {
      setUploadPhase((current) => (current === 'uploading' ? 'scanning' : current));
    }, 900);
    const body = new FormData();
    body.append('file', file);
    if (conversationId) body.append('conversation_id', conversationId);
    const headers = authHeaders();
    try {
      const response = await fetch(apiUrl('/api/documents'), {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
        signal: ac.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setUploadPhase('error');
        setUploadError(uploadErrorMessage(response.status, data));
        return;
      }
      const markReady = (doc) => {
        const id = doc?.id || data.id;
        if (id) {
          setAttachedDocs((prev) => {
            const next = [{ id, filename: doc?.filename || file.name }, ...prev.filter((row) => row.id !== id)].slice(0, 8);
            rememberPendingDocuments(user, next);
            return next;
          });
        }
        setUploadPhase('ready');
        setToolId((current) => current || 'labs');
        setValue((current) =>
          current.trim()
            ? current
            : 'I attached my report. Please read it and explain the names and numbers in plain language. What should I confirm with my oncology team, and what is worth asking at the next visit? Do not diagnose or change treatment.'
        );
      };
      if (data.extraction_status === 'complete' || data.processing === 'ready') {
        markReady(data);
        return;
      }
      setUploadPhase('reading');
      const done = await waitUntilReadable(data.id, headers, ac.signal);
      if (done.extraction_status === 'complete') {
        markReady(done);
        return;
      }
      setUploadPhase('error');
      setUploadError(
        done.extraction_status === 'failed'
          ? 'We could not read this clearly. Try a sharper photo or a text PDF.'
          : 'This file could not be attached. Try a different report.'
      );
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setUploadPhase('error');
      setUploadError(
        err?.code === 'timeout'
          ? 'This is taking longer than usual. Keep this tab open, or try a smaller PDF.'
          : 'Upload failed. Try again.'
      );
    } finally {
      clearTimeout(scanTimer);
    }
  };

  const finishVoice = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    clearInterval(tickRef.current);
    tickRef.current = null;
    const rec = recRef.current;
    recRef.current = null;
    const mime = cleanMime(rec?.mimeType);
    const blob = await new Promise((resolve) => {
      if (!rec || rec.state === 'inactive') {
        resolve(new Blob(chunksRef.current, { type: mime }));
        return;
      }
      const finish = () => resolve(new Blob(chunksRef.current, { type: mime }));
      rec.onstop = finish;
      try {
        if (typeof rec.requestData === 'function' && rec.state === 'recording') rec.requestData();
        rec.stop();
      } catch {
        finish();
      }
    });
    stopTracks(streamRef.current);
    streamRef.current = null;
    chunksRef.current = [];
    setVoicePhase('transcribing');
    const sendTranscript = (text) => {
      setVoiceError('');
      setVoicePhase('');
      if (!disabled && onSend) {
        onSend(text, sendMeta('voice'));
        setValue('');
        setToolId('');
      } else {
        setValue(text);
        ref.current?.focus();
      }
    };
    try {
      const heldMs = Date.now() - (startedAtRef.current || 0);
      if (heldMs < 1200 || !blob.size || blob.size < 2000) {
        setVoiceError('Hold Voice, speak a sentence, then tap Voice again.');
        setVoicePhase('');
        return;
      }
      const result = await transcribeVoiceNote({
        audioBase64: await blobToBase64(blob),
        mime,
      });
      const text = String(result.transcript || '').trim();
      if (!text) throw Object.assign(new Error('empty'), { status: 422 });
      sendTranscript(text);
    } catch (err) {
      if (err?.status === 402) {
        if (!isPro) setUpgradeOpen(true);
        setVoicePhase('');
        return;
      }
      try {
        setVoiceError('Speak now. Using the browser…');
        const spoken = await listenInBrowser();
        sendTranscript(spoken);
      } catch {
        setVoiceError(voiceErrorMessage(err));
        setVoicePhase('');
      }
    } finally {
      stoppingRef.current = false;
    }
  };

  const startVoice = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setVoiceError('Voice notes need a current Chrome, Edge, or Safari with a microphone.');
      return;
    }
    setVoiceError('');
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    streamRef.current = stream;
    chunksRef.current = [];
    const mime = pickRecorderMime();
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    rec.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };
    rec.start();
    recRef.current = rec;
    const startedAt = Date.now();
    startedAtRef.current = startedAt;
    setElapsed(0);
    setVoicePhase('recording');
    tickRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000);
      setElapsed(seconds);
      if (seconds >= VOICE_MAX_SEC) finishVoice();
    }, 250);
  };

  const toggleVoice = async () => {
    if (transcribing || uploading) return;
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }
    setToolsOpen(false);
    if (recording) {
      await finishVoice();
      return;
    }
    try {
      await startVoice();
    } catch (err) {
      stopTracks(streamRef.current);
      streamRef.current = null;
      setVoicePhase('');
      if (err?.name === 'NotAllowedError' || err?.name === 'NotFoundError') {
        setVoiceError('Allow the microphone to take a voice note.');
        return;
      }
      setVoiceError('Could not start the microphone. Try again.');
    }
  };

  const useTool = (item) => {
    setToolsOpen(false);
    if (item.pro && !isPro) {
      setUpgradeOpen(true);
      return;
    }
    if (item.kind === 'prompt') {
      setToolId(item.id || '');
      setValue(item.text || '');
      ref.current?.focus();
      return;
    }
    if (item.kind === 'upload') {
      fileRef.current?.click();
      return;
    }
    if (item.kind === 'route' && item.href) {
      const href = item.href;
      if (
        href.startsWith('/cost') ||
        href.startsWith('/find') ||
        href.startsWith('/subscribe') ||
        href.startsWith('/platform') ||
        href.startsWith('/for-hospitals')
      ) {
        goProduct(wwwUrl(href), navigate);
        return;
      }
      navigate(memberPath(href));
      return;
    }
    if (item.kind === 'voice') toggleVoice();
  };

  const voiceLabel = recording
    ? `Voice ${formatElapsed(elapsed)}`
    : transcribing
      ? 'Transcribing…'
      : 'Voice';

  return (
    <div className="by-composer-wrap">
      <div className={`by-composer${recording ? ' is-listening' : ''}${transcribing ? ' is-busy' : ''}`}>
        {toolsOpen ? (
          <div ref={toolsRef}>
            <ToolsMenu isPro={isPro} onPick={useTool} />
          </div>
        ) : null}
        <label className="by-composer-field">
          <span className="sr-only">Message to Vani</span>
          <textarea
            ref={ref}
            value={value}
            disabled={disabled || recording || transcribing}
            placeholder={recording ? 'Listening. Tap Voice when you are done.' : placeholder}
            rows={1}
            className="focus:outline-none focus:ring-0 focus:border-0"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
        </label>
        <div className="by-composer-row">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            hidden
            onChange={uploadReport}
          />
          <button
            type="button"
            className="by-chip"
            disabled={uploading || recording || transcribing}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Working…' : 'Upload report'}
          </button>
          <button
            type="button"
            className={`by-chip by-chip-tools${toolsOpen ? ' is-on' : ''}`}
            disabled={recording || transcribing}
            onClick={() => setToolsOpen((open) => !open)}
          >
            <IconWrench /> Tools
          </button>
          <button
            type="button"
            className={`by-chip${recording ? ' is-rec' : ''}${transcribing ? ' is-busy' : ''}`}
            disabled={uploading || transcribing}
            onClick={toggleVoice}
            aria-pressed={recording}
            title={isPro ? (recording ? 'Stop and send' : 'Record a voice note') : 'Voice is included with ByOnco PRO'}
          >
            {recording ? <span className="by-rec-dot" aria-hidden="true" /> : <IconMic />}
            {voiceLabel}
            {!isPro ? <ProBadge /> : null}
          </button>
          <button type="button" className="by-send" onClick={submit} disabled={disabled || recording || transcribing || !value.trim()} aria-label="Send">
            <IconSend />
          </button>
        </div>
      </div>
      <UploadStatus phase={uploadPhase} filename={uploadName} errorDetail={uploadError} />
      {voiceError ? (
        <p className="by-voice-status" role="status">{voiceError}</p>
      ) : null}
      {!isPro ? (
        <p className="by-fine" style={{ color: '#7a5b00' }}>
          Voice is available on PRO only. Free accounts get two lifetime text questions.
        </p>
      ) : null}
      <p className="by-fine">Vani is an assistant trained to stay inside oncology. She can be wrong. Confirm anything medical with your care team.</p>
    </div>
  );
}
